import React, { useCallback, useEffect, useRef, useState } from 'react';
import { IPresentationListing } from '../../../../entities-generated/models/presentation/presentation-listing';
import { UI } from './ui';
import { IPresentationListingDisplayModel, ListingContextMenuState } from '../../entities/listing-display-model';
import { any, firstOrDefault, forEach, none, orderBy, orderByDesc, where } from '../../../../framework/utils/collection';
import { BwjViewManager, BwjViewState } from '../../../../framework/components/bwj-view-manager';
import { AppRoutes } from '../../../app-routes';
import { xlate, xlationContext } from '../../../../framework/utils/i18n';
import { BwjButtonType } from '../../../../framework/components/bwj-button';
import { PresentationApiClient } from '../../../../services/presentation';
import { IConfirmationDialogDisplayProps } from '../../../../framework/components/bwj-confirmation-dialog';
import { useToast } from '../../../../framework/components/bwj-toast/context';
import { MessageBarType } from '@fluentui/react';
import { useNavigator } from '../../../../framework/services/navigation';
import { IShowManageSubscriptionModalArgs } from '../../components/manage-subscription-modal';
import { PresenterAccountService } from '../../../../services/account';
import { getUtcNowUnixTimestamp, HOUR_IN_MILLISECONDS } from '../../../../framework/utils/date-time';
import { PresenterAccountStatus } from '../../../../entities-generated/common/presenter-account-status';
import { useStandardErrorState } from '../../../../entities/view-manager-standard/error';
import { getBrowserLanguage } from '../../../../framework/utils/device';
import { ICreatePresentationRequest } from '../../../../entities-generated/models/presentation/create-presentation-request';
import { PresentationTiming } from '../../../../entities-generated/common/presentation-timing';
import { IShowAnnouncementBannerArgs } from '../../../../framework/components/bwj-announcement/props';
import { IPresenterTokenClaims } from '../../../../entities-generated/models/presenter-account/presenter-token-claims';
import { IBwjFormOption } from '../../../../framework/entities/bwj-form-option';
import { BwjInput } from '../../../../framework/entities/bwj-input';
import { useReffedState } from '../../../../framework/hooks/reffed-state';
import { ILaunchDiscussionListingMenuPanelArgs } from '../../components/discussion-menu-panel';
import * as formService from '../../form-services/presentation-editor';

xlationContext('My presentations page');

const MAX_ACTIVE_SUB_PRESENTATIONS = 10000;
const MAX_TRIAL_SUB_PRESENTATIONS = 15;

const daySingular = xlate('day');
const dayPlural = xlate('days');

const locale = getBrowserLanguage();

const getEffectiveAccountStatus = (account: IPresenterTokenClaims) => {
  const now = getUtcNowUnixTimestamp();
  if(now < account.accountExpirationDate) { // active account
    return account.accountStatus === PresenterAccountStatus.ActiveSubscriber || account.accountStatus === PresenterAccountStatus.InactiveSubscriber ?
      PresenterAccountStatus.ActiveSubscriber : PresenterAccountStatus.ActiveTrial;
  } else {
    return account.accountStatus === PresenterAccountStatus.ActiveSubscriber || account.accountStatus === PresenterAccountStatus.InactiveSubscriber ?
      PresenterAccountStatus.InactiveSubscriber : PresenterAccountStatus.InactiveTrial;
  }
};

const normalizeTitle = (title: string) => {
  const normalized = title.trim()
    .replaceAll(/\s+/g, ' ')
    .toLocaleLowerCase(locale);
  return normalized;
};

const getNormalizedTitles = (models: IPresentationListingDisplayModel[]) => {
  const titles = models.map(model => {
    return normalizeTitle(model.presentation.title);
  });
  return titles;
}

const getNewTitle = (existingTitles: string[]) => {
  const title = xlate('My Discussion Plan');
  const nt = normalizeTitle(title);
  if(any(existingTitles, et => et === nt)) {
    let titleCount = 2;
    while(true) {
      const t = xlate('My Discussion Plan ({titleCount})', { titleCount });
      const normTitle = normalizeTitle(t);
      if(none(existingTitles, et => et === normTitle)) {
        return t;
      }
      titleCount++;
    }
  }

  return title;
};

const getCloningTitle = (title: string, existingTitles: string[]) => {
  const copyOf = xlate('^copy\\s+of\\s+');
  const copyOfRegex = new RegExp(copyOf, 'gi');
  const copyNumOf = xlate('^copy\\s+\\(\\s*([1-9][0-9]{0,3})\\s*\\)\\s+of\\s+');
  const copyNumOfRegex = new RegExp(copyNumOf, 'gi');
  let titleRoot = '';
  let copyIterator = 0;

  // if 'copy of' prefix is on the title, try 'copy (2) of {title}'.  If it works, return it
  // we'll find a unique instance number to use later
  if(copyOfRegex.test(title)) { 
    titleRoot = title.replace(copyOfRegex, '');

    const cloneTitle = xlate('Copy (2) of {titleRoot}', { titleRoot });
    const normalized = normalizeTitle(cloneTitle);
    if(none(existingTitles, t => t === normalized)) { return cloneTitle; }
    else {
      copyIterator = 3;
    }
  } else if (copyNumOfRegex.test(title)) { // if 'copy (#) of' prefix is already on title, we'll find a unique instance number to use later
    titleRoot = title.replace(copyNumOfRegex, '');
    const result = copyNumOfRegex.exec(title);
    copyIterator = parseInt(result![1], 10) + 1;
  } else { // if neither 'copy of' nor 'copy (#) of' is already on title, try 'copy of {title}'.  If it works, return it
    const cloneTitle = xlate('Copy of {title}', { title });
    const normalized = normalizeTitle(cloneTitle);
    // this not being unique should be impossible, so if the 'impossible' happens, return an empty string
    const newTitle = none(existingTitles, t => t === normalized) ? cloneTitle : '';
    return newTitle;
  }

  // find unique iteration of 'Copy (#)' of {title}
  for(let i = copyIterator; i < 10000; i++) {
    let cloneTitle = xlate('Copy ({iteration}) of {titleRoot}', { iteration: i, titleRoot });
    if(cloneTitle.length > 50) {
      cloneTitle = cloneTitle.replace(/\.\.\.$/, '');
      if(cloneTitle.length > 47) {
        cloneTitle = cloneTitle.substring(0, 47);
      }
      cloneTitle += '...';
    }

    const normalized = normalizeTitle(cloneTitle);
    if(none(existingTitles, t => t === normalized)) { return cloneTitle; }
  }

  return ''; // this happens if we clone 'Copy (9999) of', which should never happen
}

const orderingOptions: IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>[] = [
  { value: 'chronDESC', description: xlate('Most recently used / edited') },
  { value: 'abcASC', description: xlate('Alphabetical (A to Z)') },
  { value: 'chronASC', description: xlate('Least recently used / edited') },
  { value: 'abcDESC', description: xlate('Alphabetical (Z to A)') },
];

const getOrderingOptionInput = (value: IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>) => {
  return new BwjInput<IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>>(value);
};

const orderModels = (
  models: IPresentationListingDisplayModel[],
  orderOption: BwjInput<IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>>) => {
    const order = orderOption.value.value;
    switch(order) {
      case 'abcASC': return orderBy(models, m => m.presentation.title);
      case 'abcDESC': return orderByDesc(models, m => m.presentation.title);
      case 'chronASC': return orderBy(models, m => m.orderValue);
      case 'chronDESC': return orderByDesc(models, m => m.orderValue);
    }
}

export const MyPresentationsPage: React.FunctionComponent = () => {
  const [viewState, setViewState] = useState(BwjViewState.Loading);
  const loadingMessage = useRef(xlate('Loading discussion plans...'));
  const [models, setModels] = useState<IPresentationListingDisplayModel[]>([]);
  const [orderingOption, setOrderingOption, orderingOptionRef] = useReffedState(getOrderingOptionInput(orderingOptions[0]));
  const [invokeShowAnnouncement, setInvokeShowAnnouncement] = useState<IShowAnnouncementBannerArgs | undefined>(undefined);
  const [confirmDeleteDisplay, setConfirmDeleteDisplay] = useState<IConfirmationDialogDisplayProps | undefined>(undefined);
  const [showManageSubscriptionModalArgs, setShowManageSubscriptionModalArgs] = useState<IShowManageSubscriptionModalArgs | undefined>(undefined);
  const [launchMenuPanelArgs, setLaunchMenuPanelArgs] = useState<ILaunchDiscussionListingMenuPanelArgs | undefined>(undefined);
  const [launchCloneDiscussionModal, setLaunchCloneDiscussionModal] = useState<{} | undefined>(undefined);
  const idleMenuTimeoutId = useRef(0);
  const { showToast } = useToast();
  const errorState = useStandardErrorState();
  const navigator = useNavigator();

  const onListLoaded = useCallback((list: IPresentationListing[]) => {
    if(list.length > 0) {
      const mdls = list.map(x => {
        const orderValue = x.inProgress ?
          Number.MAX_VALUE : !!x.endTime && x.lastModifiedDate < x.endTime ?
          x.endTime : x.lastModifiedDate;
        const model: IPresentationListingDisplayModel = {
          presentation: x,
          menuState: ListingContextMenuState.Closed,
          orderValue,
        };
        return model;
      });
      const orderedModels = orderModels(mdls, orderingOptionRef.current);
      
      setModels(orderedModels);
      setLaunchCloneDiscussionModal(undefined);
      setViewState(BwjViewState.Ready);
    } else {
      setViewState(BwjViewState.Untouched);
    }
  }, [orderingOptionRef]);

  const onDisplayOrderUpdate = (orderOption: IBwjFormOption<'abcASC' | 'abcDESC' | 'chronASC' | 'chronDESC'>) => {
    window.clearTimeout(idleMenuTimeoutId.current);
    const input = getOrderingOptionInput(orderOption);
    setOrderingOption(input);
    
    const orderedModels = orderModels(models, input);
    setModels(orderedModels);
  };

  const displayAnnouncements = useCallback(() => {
    PresenterAccountService.myAccount()
    .then(account => {
      const accountStatus = getEffectiveAccountStatus(account);
      if(accountStatus === PresenterAccountStatus.InactiveSubscriber) {
        setInvokeShowAnnouncement({
          messageType: MessageBarType.blocked,
          message: xlate('<strong>The status of your subscription has changed.</strong> Access to your discussion plans is limited. Please review your account status on the <a href="/subscription">Manage My Subscription</a> page'),
          renderAsHtml: true,
          dismissable: true,
        });
      } else if(accountStatus === PresenterAccountStatus.InactiveTrial) {
        setInvokeShowAnnouncement({
          messageType: MessageBarType.blocked,
          message: xlate('<strong>Your trial subscription has ended.</strong> To continue using Presentual, please <a href="/subscription">select the plan</a> that works best for you'),
          renderAsHtml: true,
          dismissable: true,
          icon: 'Info',
        });
      } else if(accountStatus === PresenterAccountStatus.ActiveTrial) {
        const now = getUtcNowUnixTimestamp();
        const hoursRemaining = (account.accountExpirationDate - now) / HOUR_IN_MILLISECONDS;
        if(hoursRemaining < 24) {
          setInvokeShowAnnouncement({
            messageType: MessageBarType.warning,
            message: xlate('<strong>Your trial subscription ends soon.</strong> To continue using Presentual without interruption, please <a href="/subscription">select the plan</a> that works best for you'),
            renderAsHtml: true,
            dismissable: true,
          });
        } else {
          const daysRemaining = Math.floor(hoursRemaining / 24);
          const dayWord = daysRemaining < 2 ? daySingular : dayPlural;
          setInvokeShowAnnouncement({
            messageType: MessageBarType.info,
            message: xlate('<strong>Thanks for checking us out!</strong> You have {daysRemaining} {dayWord} remaining on your trial subscription', { daysRemaining, dayWord }),
            renderAsHtml: true,
            dismissable: true,
          });
        }
      }
    })
    .catch(() => {});
  }, []);

  useEffect(() => {
    PresentationApiClient.getAccountPresentationList()
    .then(onListLoaded)
    .then(displayAnnouncements)
    .catch(() => {
      setViewState(BwjViewState.Error);
    });
  }, [onListLoaded, displayAnnouncements]);

  const setMenuStates = (presentation: IPresentationListing, state: ListingContextMenuState) => {
    const model = firstOrDefault(models, m => m.presentation.id === presentation.id)!;
    const transientModels = where(models, m => m.menuState === ListingContextMenuState.Opening || m.menuState === ListingContextMenuState.Closing);

    forEach(transientModels, m => {
      m.menuState = m.menuState === ListingContextMenuState.Opening ?
        ListingContextMenuState.Open : ListingContextMenuState.Closed;
    });
    model.menuState = state;

    setModels([...models]);
  };

  const onWillOpenContextMenu = (presentation: IPresentationListing, usePanel: boolean) => {
    const model = firstOrDefault(models, m => m.presentation.id === presentation.id)!;
    const openModels = where(models, m => m.menuState === ListingContextMenuState.Open);
    const transientModels = where(models, m => m.menuState === ListingContextMenuState.Opening || m.menuState === ListingContextMenuState.Closing);

    if(usePanel) {
      setLaunchMenuPanelArgs({ presentation });
    } else {
      model.menuState = ListingContextMenuState.Opening;
    }
    forEach(openModels, m => { m.menuState = ListingContextMenuState.Closing; });
    forEach(transientModels, m => {
      m.menuState = m.menuState === ListingContextMenuState.Opening ?
        ListingContextMenuState.Open : ListingContextMenuState.Closed;
    });

    setModels([...models]);
  };
  const onWillCloseContextMenu = (presentation: IPresentationListing) =>
    setMenuStates(presentation, ListingContextMenuState.Closing);
  const onContextMenuOpened = (presentation: IPresentationListing) => {
    setMenuStates(presentation, ListingContextMenuState.Open);
    
    idleMenuTimeoutId.current = window.setTimeout(() => {
      setMenuStates(presentation, ListingContextMenuState.Closing);
    }, 15000);
  }
  const onContextMenuClosed = (presentation: IPresentationListing) => 
    setMenuStates(presentation, ListingContextMenuState.Closed);

  const discoverIfSubscriptionActive = () => {
    setViewState(BwjViewState.Loading);

    return PresenterAccountService.myAccount()
    .then(account => {
      const accountStatus = getEffectiveAccountStatus(account);
      if(accountStatus === PresenterAccountStatus.ActiveSubscriber || accountStatus === PresenterAccountStatus.ActiveTrial) {
        return true;
      } else {
        const trialSubscription = account.accountStatus === PresenterAccountStatus.InactiveTrial;
        setShowManageSubscriptionModalArgs({ trialSubscription });
        return false;
      }
    })
    .catch(() => {
      setViewState(BwjViewState.Error);
      return Promise.resolve(false);
    });
  };

  const onEditPresentation = (presentation: IPresentationListing) => {
    window.clearTimeout(idleMenuTimeoutId.current);
    discoverIfSubscriptionActive()
    .then(isActive => {
      if(isActive) {
        navigator.goTo(AppRoutes.updatePresentation.url(presentation.id));
      }
    });
  };

  const onBeginPresentation = (presentation: IPresentationListing) => {
    window.clearTimeout(idleMenuTimeoutId.current);
    discoverIfSubscriptionActive()
    .then(isActive => {
      if(isActive) {
        navigator.goTo(AppRoutes.performPresentation.url(presentation.id));
      }
    });
  };

  const onEndPresentation = (presentation: IPresentationListing) => {
    window.clearTimeout(idleMenuTimeoutId.current);
    setMenuStates(presentation, ListingContextMenuState.Closing);

    PresentationApiClient.stopPresentation(presentation.id)
    .then(() => {
      const model = firstOrDefault(models, m => m.presentation.id === presentation.id);
      if(!!model) {
        model.presentation.inProgress = false;
        setModels([...models]);
      }

      showToast(MessageBarType.info, xlate('Discussion "{presentation}" ended', { presentation: presentation.title}));
    })
    .catch(() => {
      showToast(MessageBarType.error, xlate('An error occurred'));
    });
  };

  const onPerformDelete = (presentation: IPresentationListing) => {
    setConfirmDeleteDisplay(undefined);
    setViewState(BwjViewState.Loading);

    PresentationApiClient.deletePresentation(presentation.id)
    .then(() => PresentationApiClient.getAccountPresentationList())
    .then(list => {
      onListLoaded(list);
      showToast(MessageBarType.info, xlate('Discussion plan {presentation} was deleted', { presentation: presentation.title }));
    })
    .catch(() => showToast(MessageBarType.error, xlate('An error occurred')));
  };

  const onCancelDeletion = () => {
    setConfirmDeleteDisplay(undefined);
  };

  const onDeletePresentation = (presentation: IPresentationListing) => {
    window.clearTimeout(idleMenuTimeoutId.current);
    setMenuStates(presentation, ListingContextMenuState.Closing);

    const displayProps: IConfirmationDialogDisplayProps = {
      context: presentation,
      title: xlate('Delete Discussion Plan'),
      message: xlate('You are about to delete discussion plan *{presentation}*.  This action cannot be undone.  Are you sure that you wish to proceed?', { presentation: presentation.title }),
      proceedPrompt: xlate('Delete plan'),
      cancelPrompt: xlate('Cancel'),
      onProceedRequested: onPerformDelete,
      onCancelRequested: onCancelDeletion,
    };
    setConfirmDeleteDisplay(displayProps);
  };

  const onCloneDiscussionRequested = (presentation: IPresentationListing) => {
    window.clearTimeout(idleMenuTimeoutId.current);
    setMenuStates(presentation, ListingContextMenuState.Closing);
    loadingMessage.current = xlate('Preparing to copy...');
    setViewState(BwjViewState.Loading);

    PresentationApiClient.getPresentationForEditing(presentation.id)
      .then(dataModel => {
        dataModel.model.title = getCloningTitle(dataModel.model.title, dataModel.existingTitles);
        formService.initializeService(dataModel);

        setLaunchCloneDiscussionModal({});
      })
      .catch(() => {
        showToast(MessageBarType.error, xlate('An error occurred'));
      })
      .finally(() => setViewState(BwjViewState.Ready));
  }

  const onCreateDiscussionClone = () => {
    window.clearTimeout(idleMenuTimeoutId.current);
    loadingMessage.current = xlate('Creating copy...')
    discoverIfNewCreationAllowed()
    .then(goodToGo => {
      if(goodToGo === false) { return undefined; }
      
      const request = formService.toCreateRequest();
      return PresentationApiClient.createPresentation(request);
    })
    .then(id => {
      if(id === undefined) { return; }
      navigator.goTo(AppRoutes.updatePresentation.url(id.value));
    })
    .catch((e) => {
      showToast(MessageBarType.error, xlate('An error occurred'));
      setViewState(BwjViewState.Ready);
    });
  };

  const discoverIfNewCreationAllowed = () => {
    return discoverIfSubscriptionActive()
    .then(isActive => {
      if(isActive) {
        return PresenterAccountService.myAccount();
      } else {
        return undefined;
      }
    })
    .then(claims => {
      if(claims !== undefined) {
        if(claims.accountStatus === PresenterAccountStatus.ActiveSubscriber && models.length >= MAX_ACTIVE_SUB_PRESENTATIONS) {
          showToast(MessageBarType.blocked, xlate('You have reached or exceeded the maximum number of discussion plans allowed per subscription'), true);
          return false;
        } else if(claims.accountStatus === PresenterAccountStatus.ActiveTrial && models.length >= MAX_TRIAL_SUB_PRESENTATIONS) {
          showToast(MessageBarType.blocked, xlate('You are going strong, but... only up to {maxSubCount} discussion plans can be created on a trial subscription. Please select Manage My Subscription from the main menu, and choose the plan that is right for you.'), true);
          return false;
        } else {
          return true;
        }
      }
    })
    .catch(() => {
      showToast(MessageBarType.error, xlate('An error occurred'));
      setViewState(BwjViewState.Ready);
      return Promise.resolve(false);
    });
  }

  const onCreateNewPresentation = () => {
    window.clearTimeout(idleMenuTimeoutId.current);
    loadingMessage.current = xlate('Creating new discussion plan...')
    discoverIfNewCreationAllowed()
    .then(goodToGo => {
      if(goodToGo === false) { return undefined; }
      
      const titles = getNormalizedTitles(models);
      const title = getNewTitle(titles);
      const request: ICreatePresentationRequest = {
        timing: PresentationTiming.None,
        title,
        content: '',
      };
      return PresentationApiClient.createPresentation(request);
    })
    .then(id => {
      if(id === undefined) { return; }
      navigator.goTo(AppRoutes.updatePresentation.url(id.value));
    })
    .catch((e) => {
      showToast(MessageBarType.error, xlate('An error occurred'));
      setViewState(BwjViewState.Ready);
    });
  };

  return (
    <BwjViewManager
      state={viewState}
      untouchedState={{
        icon: AppRoutes.myPresentations.icon,
        message: xlate('No discussion plans at the moment'),
        buttons: [{
          buttonStyle: BwjButtonType.PrimaryStandard,
          text: xlate('Create a new discussion plan'),
          onClick: onCreateNewPresentation,
        }],
      }}
      loadingState={{
        message: loadingMessage.current,
      }}
      errorState={errorState}
    >
      <UI
        presentations={models}
        orderingOptionInput={orderingOption}
        orderingOptions={orderingOptions}
        onOrderingOptionUpdate={onDisplayOrderUpdate}
        onWillOpenContextMenu={onWillOpenContextMenu}
        onContextMenuOpened={onContextMenuOpened}
        onContextMenuClosed={onContextMenuClosed}
        onEditPresentation={onEditPresentation}
        onCloneDiscussionRequested={onCloneDiscussionRequested}
        onCreateDiscussionClone={onCreateDiscussionClone}
        onBeginPresentation={onBeginPresentation}
        onEndPresentation={onEndPresentation}
        onDeletePresentation={onDeletePresentation}
        onWillCloseContextMenu={onWillCloseContextMenu}
        confirmDeleteDisplay={confirmDeleteDisplay}
        onCreatePresentation={onCreateNewPresentation}
        showManageSubscriptionModalArgs={showManageSubscriptionModalArgs}
        invokeShowAnnouncement={invokeShowAnnouncement}
        launchMenuPanelArgs={launchMenuPanelArgs}
        launchCloneDiscussionModal={launchCloneDiscussionModal}
      />
    </BwjViewManager>
  );
};
